import { useMemo } from 'react';
import {
  ArrowsClockwiseIcon,
  CheckIcon,
  CircleNotchIcon,
  SparkleIcon,
} from '@phosphor-icons/react';
import { T, Var, useGT } from 'gt-react';
import type { WorkbookSopUpdate } from '@erp/rpc';
import type { WorkbookDefinition } from '@erp/shared-types';
import { workbookReferences, type WorkbookReference } from '@erp/workbooks';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { WordDiff } from '@/components/common/WordDiff';
import { useSopUpdates } from './useWorkbookSopUpdates';

/** A Reference as its Item's name, the way the Instruction reads on screen. */
function referenceName(reference: WorkbookReference) {
  return reference.kind === 'missing' ? '?' : reference.name;
}

/**
 * Regenerates Instructions from their settings: opens the preview of their
 * rewrites, writing any the review has not proposed yet. `stepIds` are the
 * Instructions it covers; with none left to rewrite it renders nothing.
 */
export function WorkbookRegenerateSopButton({
  stepIds,
  whole = false,
  variant = 'outline',
  disabled = false,
}: {
  stepIds: readonly string[];
  /** Covers a whole SOP ("Regenerate SOP") rather than one Instruction. */
  whole?: boolean;
  variant?: 'outline' | 'ghost';
  disabled?: boolean;
}) {
  const gt = useGT();
  const sop = useSopUpdates();
  const covered = stepIds.filter((id) => sop?.candidates.includes(id));
  if (!sop || !covered.length) return null;
  const ready = covered.every((id) => sop.proposals.has(id));
  return (
    <Button
      size="sm"
      variant={variant}
      disabled={disabled || covered.some((id) => sop.regenerating.has(id))}
      onClick={() => void sop.regenerate(covered)}
      aria-label={
        whole
          ? gt('Regenerate SOP', { context: 'workbook sop regenerate all' })
          : gt('Regenerate instruction', {
              context: 'workbook sop regenerate one',
            })
      }
    >
      {ready ? <SparkleIcon /> : <ArrowsClockwiseIcon />}
      {whole ? (
        <T context="workbook sop regenerate all">Regenerate SOP</T>
      ) : (
        <T context="workbook sop regenerate one">Regenerate instruction</T>
      )}
      {ready && (
        <Badge variant="secondary" className="ml-1 text-[11px]">
          <T context="workbook sop update ready badge">Ready</T>
        </Badge>
      )}
    </Button>
  );
}

/** Why the automatic SOP update of one Instruction did not happen, where its review reached no verdict. */
export function WorkbookSopReviewUnavailable({ stepId }: { stepId: string }) {
  const review = useSopUpdates()?.unavailable.get(stepId);
  if (!review) return null;
  return (
    <span role="status" className="mt-1 block text-xs">
      {review.reason === 'rewrite-failed' ? (
        <T context="workbook sop review unavailable: writer">
          The AI couldn't write an update after the last check, so this
          instruction wasn't updated. Regenerate it, or edit it yourself.
        </T>
      ) : review.trigger === 'run' ? (
        <T context="workbook sop review unavailable: run">
          The AI check after the last run didn't answer, so this instruction
          wasn't updated. Regenerate it, or edit it yourself.
        </T>
      ) : (
        <T context="workbook sop review unavailable: save">
          The AI check after the last save didn't answer, so this instruction
          wasn't updated. Regenerate it, or edit it yourself.
        </T>
      )}
      {review.matchDetail && (
        <span className="ml-1 text-muted-foreground">
          {`(${review.matchDetail})`}
        </span>
      )}
    </span>
  );
}

/** The preview of SOP updates: each Instruction's current text beside its rewrite, to Accept or Discard. */
export function WorkbookSopUpdateDialog({
  definition,
}: {
  definition: WorkbookDefinition;
}) {
  const gt = useGT();
  const sop = useSopUpdates();
  const references = useMemo(
    () => workbookReferences(definition),
    [definition],
  );
  if (!sop) return null;
  const ids = sop.previewIds ?? [];
  const ready = ids.flatMap((id) => {
    const update = sop.proposals.get(id);
    return update ? [update] : [];
  });
  const text = (markdown: string) =>
    references.replace(markdown, referenceName).trim();
  return (
    <Dialog
      open={sop.previewIds !== null}
      onOpenChange={(open) => !open && sop.closePreview()}
    >
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>
            <T context="workbook sop update dialog title">
              Review updated instructions
            </T>
          </DialogTitle>
          <DialogDescription>
            <T context="workbook sop update dialog description">
              The settings changed after these instructions were written.
              Compare each rewrite with the current text. Accepting replaces the
              text, and you can undo it.
            </T>
          </DialogDescription>
        </DialogHeader>
        <ul
          className="flex flex-col gap-3"
          aria-label={gt('Updated instructions', {
            context: 'workbook sop update list',
          })}
        >
          {ids.map((id) => (
            <SopUpdateItem
              key={id}
              stepId={id}
              definition={definition}
              text={text}
            />
          ))}
        </ul>
        {sop.error && (
          <p role="alert" className="text-sm text-destructive">
            {sop.error}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={sop.closePreview}>
            <T context="dialog close button">Close</T>
          </Button>
          {ready.length > 1 && (
            <Button onClick={() => sop.accept(ready)}>
              <CheckIcon />
              <T context="workbook sop update accept all">
                Accept all (<Var>{ready.length}</Var>)
              </T>
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** One Instruction in the preview: its rewrite with Accept / Discard, or why there is none yet. */
function SopUpdateItem({
  stepId,
  definition,
  text,
}: {
  stepId: string;
  definition: WorkbookDefinition;
  text: (markdown: string) => string;
}) {
  const gt = useGT();
  const sop = useSopUpdates();
  const step = definition.processSteps.find((item) => item.id === stepId);
  const update = sop?.proposals.get(stepId);
  const title =
    step?.title ||
    text(step?.instructions.split('\n')[0] ?? '') ||
    gt('Instruction', { context: 'workbook unnamed sop step' });
  return (
    <li className="rounded-lg border border-input">
      <div className="flex flex-wrap items-center gap-2 border-b border-input bg-muted/40 px-3 py-2">
        <h3 className="min-w-0 flex-1 text-sm font-medium">{title}</h3>
        {update && <SopUpdateActions update={update} title={title} />}
      </div>
      {update ? (
        <SopUpdateComparison update={update} text={text} />
      ) : (
        <SopUpdatePending writing={sop?.regenerating.has(stepId) === true} />
      )}
    </li>
  );
}

function SopUpdateActions({
  update,
  title,
}: {
  update: WorkbookSopUpdate;
  title: string;
}) {
  const gt = useGT();
  const sop = useSopUpdates();
  return (
    <>
      {update.probability !== null && (
        <Badge variant="outline" className="font-normal">
          <T context="workbook sop update match badge">
            <Var>{Math.round(update.probability * 100)}</Var>% match
          </T>
        </Badge>
      )}
      <Button
        size="sm"
        variant="ghost"
        onClick={() => sop?.discard(update)}
        aria-label={gt('Discard update to {name}', {
          context: 'workbook sop update discard',
          name: title,
        })}
      >
        <T context="workbook sop update discard">Discard</T>
      </Button>
      <Button
        size="sm"
        onClick={() => sop?.accept([update])}
        aria-label={gt('Accept update to {name}', {
          context: 'workbook sop update accept',
          name: title,
        })}
      >
        <CheckIcon />
        <T context="workbook sop update accept">Accept</T>
      </Button>
    </>
  );
}

function SopUpdatePending({ writing }: { writing: boolean }) {
  if (writing)
    return (
      <p
        role="status"
        className="flex items-center gap-2 p-3 text-sm text-muted-foreground"
      >
        <CircleNotchIcon className="size-4 animate-spin" />
        <T context="workbook sop update writing">
          Writing an updated instruction…
        </T>
      </p>
    );
  return (
    <p className="p-3 text-sm text-muted-foreground">
      <T context="workbook sop update none">No update for this instruction.</T>
    </p>
  );
}

function SopUpdateComparison({
  update,
  text,
}: {
  update: WorkbookSopUpdate;
  text: (markdown: string) => string;
}) {
  const before = text(update.baseInstructions);
  const after = text(update.proposedInstructions);
  return (
    <div className="flex flex-col gap-2 p-3">
      {update.summary && (
        <p className="text-sm text-muted-foreground">{update.summary}</p>
      )}
      {update.proposedTitle && update.proposedTitle !== update.baseTitle && (
        <p className="text-sm">
          <T context="workbook sop update new title">
            New title: <Var>{update.proposedTitle}</Var>
          </T>
        </p>
      )}
      <div className="grid gap-2 md:grid-cols-2">
        <section className="min-w-0">
          <h4 className="mb-1 text-xs font-medium tracking-wide text-muted-foreground uppercase">
            <T context="workbook sop update current text">Current</T>
          </h4>
          <p className="rounded-md bg-destructive/5 p-2 text-sm whitespace-pre-wrap break-words">
            <WordDiff before={before} after={after} side="before" />
          </p>
        </section>
        <section className="min-w-0">
          <h4 className="mb-1 text-xs font-medium tracking-wide text-muted-foreground uppercase">
            <T context="workbook sop update proposed text">Proposed</T>
          </h4>
          <p className="rounded-md bg-emerald-500/5 p-2 text-sm whitespace-pre-wrap break-words">
            <WordDiff before={before} after={after} side="after" />
          </p>
        </section>
      </div>
    </div>
  );
}
